// SitePeek — the ONE site peek component, TWO view bodies keyed on the list's
// active tab (?view= — the locked Registry ≠ Solution split), rendered by the
// global PeekHost (`?peek=sites:<id>`; ADR-036 — the page only writes the
// ref). The view arrives from the SAME URL (read here, never a prop): opened
// away from the sites list (no ?view=) it shows the Registry face.
//   • REGISTRY — the record at a glance (identity · classification · location
//     read sections + the responsibilities read list + the classification
//     history, every row with its ⋯ verbs).
//   • SOLUTION — the reconciliation body (suggestions box · missing data ·
//     current config · history with the verbs · Ignored).
// BOTH faces wear ONE top row + ONE door, composed by the panel from the site's
// ONE registry (`useSiteActions`, ADR-036 §7): the top row = a draft's Resume
// setup (the primary — the wizard IS the draft's page) + the Edit menu (Edit
// site · Update classification · Update config · Manage responsibilities ·
// Correct dates — the two timelines' choosers open the same forms the body's
// history rows do), the footer = the labelled Actions door (the lifecycle) +
// "Open full page". The overlays are mounted ONCE beside the panel. Every
// mutation invalidates the sites prefix so the list + peek refetch together.

import { useTranslation } from 'react-i18next';
import type { SiteDetail } from '@ems-app/schemas/sites/responses';

import { ContextPanel } from '../../components/patterns/ContextPanel.js';
import { DescriptionList, Field } from '../../components/patterns/DescriptionList.js';
import { DetailSection } from '../../components/patterns/DetailSection.js';
import { ReconcilePeekBody, displayEnd } from '../../components/patterns/ReconcilePeekBody.js';
import { SiteStatusBadge, CopyableCode, SolutionCell, areaLine } from '../../components/patterns/sitesDisplay.js';
import { FollowStar } from '../../components/patterns/FollowStar.js';
import { useFollowSite } from '../../hooks/useFollows.js';
import { toConfigValues } from '../../components/patterns/solutionConfig.js';
import { useToast } from '../../components/ui/Toast.js';
import { icons } from '../../components/ui/icons.js';
import { useCan } from '../../hooks/useCan.js';
import { useEntitlements } from '../../hooks/useEntitlements.js';
import { useIgnoreSuggestions, useRestoreSuggestion, useSiteDetail } from '../../hooks/useSites.js';
import { formatDate, formatNumber } from '../../lib/format.js';
import { useDisplayZone } from '../../hooks/useDisplayZone.js';
import { formatValue } from '../../lib/formatValue.js';
import { useUrlState, url } from '../../hooks/useUrlState.js';
import { Alert } from '../../components/ui/Alert.js';
import { PageLoader } from '../../components/ui/PageLoader.js';
import { DesignationPeriodMenu, designationValuesLine } from './DesignationsFlow.js';
import { LifecycleDayFields } from './SiteLifecycle.js';
import { useSiteActions } from './SiteActions.js';
import { ScopeChainsSection } from '../../components/patterns/ScopeChainsSection.js';
import { useEntityPeek } from '../../hooks/useEntityPeek.js';
import { useRefusalToast } from '../../components/patterns/forms/useQuickSave.js';

export interface SitePeekProps {
  projectId: string;
  siteId: string;
  onClose: () => void;
}

type View = 'registry' | 'solution';

// The list's `?view=` key, read directly (the peek serves the active face; the
// value is page view-state the list owns — the peek only observes it).
const VIEW_KEYS = { view: url.text() };

export function SitePeek({ projectId, siteId, onClose }: SitePeekProps) {
  const [{ view: viewParam }] = useUrlState(VIEW_KEYS);
  const view: View = viewParam === 'solution' ? 'solution' : 'registry';
  const { t } = useTranslation('sites');
  const detail = useSiteDetail(projectId, siteId);

  if (detail.isPending) {
    return (
      <ContextPanel host="peek" onClose={onClose} leading={<icons.site size={22} aria-hidden className="text-[var(--color-text-secondary)]" />} kind={t('peek.kind')} title="…">
        <PageLoader />
      </ContextPanel>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <ContextPanel host="peek" onClose={onClose} leading={<icons.site size={22} aria-hidden className="text-[var(--color-text-secondary)]" />} kind={t('peek.kind')} title="…">
        <Alert variant="danger">{t('list.error')}</Alert>
      </ContextPanel>
    );
  }
  return <SitePeekBody projectId={projectId} site={detail.data} view={view} onClose={onClose} />;
}

// The loaded peek — its own component so the acts hook runs on a site that is there.
function SitePeekBody({ projectId, site: s, view, onClose }: { projectId: string; site: SiteDetail; view: View; onClose: () => void }) {
  const { t, i18n } = useTranslation('sites');
  const toast = useToast();
  const can = useCan(projectId);
  const canWrite = can('site.solution_config:write');
  const canEditRecord = can('site:write');
  const hasFieldForce = useEntitlements(projectId).has('has_field_force');
  const followSite = useFollowSite(projectId);
  const ignore = useIgnoreSuggestions(projectId);
  const restore = useRestoreSuggestion(projectId);
  // The peek's one zone (ADR-082 §2): the site's own, the project's for a draft without one.
  const display = useDisplayZone({ kind: 'site', projectId, zone: s.timezone_iana });
  const { acts, overlays, designations, solution } = useSiteActions(projectId, s, { onGone: onClose });
  // A person on a chain is followed inside the sheet — the site joins the way back.
  const { followPeek } = useEntityPeek();
  const peekPerson = (userId: string) => followPeek(`users:${userId}`, s.name);

  const history = s.solution_history;
  const current = history.find((p) => p.is_current) ?? null;
  const currentDesignation = s.designation_history.find((p) => p.is_current) ?? null;
  // The classification history's row menus share the act's ONE overlay state; a draft classifies in
  // the wizard, so the menus only mount on non-drafts with site:write.
  const designationVerbs = canEditRecord && s.status !== 'draft';
  const suggestions = s.suggestions;
  const config = current ? toConfigValues(current) : null;

  const fail = useRefusalToast('site-peek');

  return (
    <>
      <ContextPanel
        host="peek"
        onClose={onClose}
        // The LEADING seat carries the star (the ONE follow control — the tickets precedent).
        leading={<FollowStar followed={s.followed} onToggle={() => followSite.mutate({ siteId: s.id, followed: !s.followed }, { onError: fail })} size={20} />}
        kind={t('peek.kind')}
        title={s.name}
        // BOTH faces wear the site's own lifecycle status: observations are findings about the record, and they
        // read in the body, never as the entity's state.
        status={<SiteStatusBadge status={s.status} t={t} />}
        openFullPath={
          // A draft has no meaningful "full page" — its home IS the setup wizard
          // (reached via Resume setup); a second "Open full page" is redundant.
          s.status === 'draft' ? undefined : `/p/${projectId}/sites/${s.id}`
        }
        acts={acts}
      >
        {/* Draft setup progress — above the record, and only while something is
            still missing (a complete draft shows nothing here). */}
        {view === 'registry' && s.status === 'draft' && s.completeness.missing.length > 0 && (
          <p className="text-sm text-[var(--color-text-secondary)]">
            {t('setup.label', { done: formatNumber(s.completeness.done), total: formatNumber(s.completeness.total) })} ·{' '}
            {t('setup.missing', { items: s.completeness.missing.map((k) => t(`setup.items.${k}`)).join(', ') })}
          </p>
        )}
        {/* Identity at a glance — both views share the anchor section. */}
        <DetailSection dense title={t('detail.sections.identity')}>
          <DescriptionList>
            {/* Solution leads Identity — the at-a-glance signal (glyph strip +
                hover breakdown), fed the current period directly (a SolutionConfig
                is a structural superset of the summary SolutionCell reads).
                Registry-only; the Solution view shows the full config below. */}
            {view === 'registry' && (
              <Field label={t('detail.fields.solution')}>
                <SolutionCell solution={s.solution_config} t={t} />
              </Field>
            )}
            <Field label={t('filters.type')}>{s.site_type?.name ?? '—'}</Field>
            <Field label={t('filters.cluster')}>
              <span>
                {s.cluster.name}
                <span className="text-[var(--color-text-secondary)]"> · {s.zone.name}</span>
              </span>
            </Field>
            {(s.reference || s.reference_alt) && (
              // Both refs under ONE "Reference" title, each on its own line,
              // right-aligned like the other values; the whole code is a
              // click-to-copy button (CopyableValue) with an always-visible icon.
              <Field label={t('detail.fields.reference')}>
                <span className="flex flex-col items-end gap-0.5">
                  {s.reference && <CopyableCode value={s.reference} t={t} iconAlways />}
                  {s.reference_alt && <CopyableCode value={s.reference_alt} t={t} iconAlways />}
                </span>
              </Field>
            )}
            {/* Classification folded into Identity on the registry view — one section
                instead of two (the solution view keeps just the identity anchor); the
                page's Classification card carries the act. ONE "Since {date}" covers
                the trio (a period is the complete classification state — ADR-060 §3). */}
            {view === 'registry' && (
              <>
                <Field label={t('filters.priority')}>{s.site_priority?.name ?? '—'}</Field>
                {s.site_category && <Field label={t('filters.category')}>{s.site_category.name}</Field>}
                {currentDesignation && (
                  <Field label={t('designations.sinceLabel')}>
                    <span className="text-[var(--color-text-secondary)]">{formatDate(currentDesignation.effective_from, 'date')}</span>
                  </Field>
                )}
                <Field label={t('detail.fields.indoor')}>{t(`detail.indoorValue.${s.is_indoor}`)}</Field>
                <Field label={t('filters.tag')}>{s.tags.length > 0 ? s.tags.map((tg) => tg.name).join(', ') : '—'}</Field>
              </>
            )}
            {/* The lifecycle days — the same set the page's Lifecycle card reads. */}
            <LifecycleDayFields site={s} t={t} />
          </DescriptionList>
        </DetailSection>

        {view === 'registry' ? (
          <>
            <DetailSection dense title={t('detail.sections.location')}>
              <DescriptionList>
                <Field label={t('detail.fields.country')}>{s.country_iso_code ?? '—'}</Field>
                <Field label={t('detail.fields.timezone')}>{s.timezone_iana ?? '—'}</Field>
                <Field label={t('detail.fields.administrative_area')}>{s.administrative_area ? areaLine(s.administrative_area, s.country_iso_code, i18n.language) : '—'}</Field>
                <Field label={t('detail.fields.coordinates')}>
                  {s.latitude != null && s.longitude != null ? (
                    <span dir="ltr">{`${formatValue(s.latitude, 'coordinate')?.display}, ${formatValue(s.longitude, 'coordinate')?.display}`}</span>
                  ) : (
                    '—'
                  )}
                </Field>
              </DescriptionList>
            </DetailSection>
            {hasFieldForce && (
              <DetailSection dense title={t('detail.sections.responsibilities')}>
                {/* The rows are content — the people on them, references; the act is the Edit menu's. */}
                <ScopeChainsSection projectId={projectId} scope={{ level: 'site', id: s.id }} peekPerson={peekPerson} />
              </DetailSection>
            )}
            {/* The designation history — every row carries its ⋯ verbs (one
                timeline grammar app-wide; the row menus share the act's overlay
                state). */}
            {s.designation_history.length > 0 && (
              <DetailSection dense title={t('designations.history')}>
                <ol className="m-0 flex list-none flex-col gap-2 p-0">
                  {s.designation_history.map((p) => (
                    <li key={p.id} className="flex items-start justify-between gap-2 text-sm">
                      <div className="min-w-0">
                        <span className={p.effective_to === null ? 'font-medium' : ''}>
                          {p.effective_to === null
                            ? t('reconcile.peek.current', { date: formatDate(p.effective_from, 'date') })
                            : t('reconcile.peek.previous', {
                                from: formatDate(p.effective_from, 'date'),
                                to: formatDate(displayEnd(p.effective_to), 'date'),
                              })}
                        </span>
                        <div className="mt-0.5 text-xs text-[var(--color-text-secondary)]">{designationValuesLine(p)}</div>
                        {p.change_reason && <div className="mt-0.5 text-xs text-[var(--color-text-secondary)]">{p.change_reason}</div>}
                      </div>
                      {designationVerbs && <DesignationPeriodMenu period={p} deleteBlocked={designations.deleteBlocked} onOpen={designations.openOverlay} t={t} />}
                    </li>
                  ))}
                </ol>
              </DetailSection>
            )}
          </>
        ) : (
          <ReconcilePeekBody
            status={s.status}
            timeZone={display.zone ?? display.viewerZone}
            suggestions={suggestions}
            config={config}
            history={history}
            canWrite={canWrite}
            onOpenForm={(mode, periodId, focusField) => solution.openOverlay({ kind: 'form', mode, periodId, focusField })}
            onStartDate={(periodId) => solution.openOverlay({ kind: 'startDate', periodId })}
            onDelete={(periodId) => solution.openOverlay({ kind: 'delete', periodId })}
            onDismiss={(field) => ignore.mutate({ siteId: s.id, field }, { onSuccess: () => toast.success(t('reconcile.toast.dismissed')), onError: fail })}
            onRestore={(field) => restore.mutate({ siteId: s.id, field }, { onSuccess: () => toast.success(t('reconcile.toast.restored')), onError: fail })}
            deleteBlocked={solution.deleteBlocked}
            t={t}
          />
        )}
      </ContextPanel>
      {overlays}
    </>
  );
}
